import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { useToaster } from '../shared/composables/use-toaster';
import { SiteId } from '../site-detail/models/Site';

import { VIDEO_CALL_STATUS, VideoCall } from './models/video-call';
import { VideoCallsGateway } from './video-calls.gateway';

/**
 * The installer's video calls, one per installation, kept up to date by the socket events.
 * Loaded once the installer is signed in. There is no session reset: logging out leaves the application.
 */
@Injectable({ providedIn: 'root' })
export class VideoCallsStore {
    private readonly gateway = inject(VideoCallsGateway);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    private readonly calls = signal<Record<SiteId, VideoCall>>({});

    callFor(siteId: SiteId): VideoCall | undefined {
        return this.calls()[siteId];
    }

    async load(): Promise<void> {
        try {
            const videoCalls = await this.gateway.list();
            if (!Array.isArray(videoCalls)) return;

            this.calls.set(
                Object.fromEntries(
                    videoCalls.map((call) => [
                        call.siteId,
                        { ...call, status: call.status ?? VIDEO_CALL_STATUS.INVITATION_SENT },
                    ]),
                ),
            );
        } catch (error) {
            console.error(error);
        }
    }

    /** Rejects when the backend refuses: the caller reports it. */
    async invite(siteId: SiteId): Promise<void> {
        const call = await this.gateway.start(siteId);
        if (!this.calls()[siteId]) {
            this.put({ ...call, status: VIDEO_CALL_STATUS.INVITATION_SENT });
        }
        this.notifyInvitationSent();
    }

    /** Rejects when the backend refuses: the caller reports it. */
    async resend(call: VideoCall): Promise<void> {
        const resent = await this.gateway.resend(call.id);
        this.invitationSent({
            ...resent,
            siteId: call.siteId,
            status: resent.status ?? VIDEO_CALL_STATUS.INVITATION_SENT,
        });
    }

    invitationSent(call: VideoCall): void {
        this.put(call);
        this.notifyInvitationSent();
    }

    participantJoined(siteId: SiteId, isOwner: boolean): void {
        this.setStatus(siteId, isOwner ? VIDEO_CALL_STATUS.IN_PROGRESS : VIDEO_CALL_STATUS.AVAILABLE);
    }

    participantLeft(siteId: SiteId, isOwner: boolean): void {
        this.setStatus(siteId, isOwner ? VIDEO_CALL_STATUS.AVAILABLE : VIDEO_CALL_STATUS.IN_PROGRESS);
    }

    stopped(siteId: SiteId): void {
        if (!this.calls()[siteId]) return;

        this.calls.update((calls) => {
            const remaining = { ...calls };
            delete remaining[siteId];
            return remaining;
        });
    }

    private setStatus(siteId: SiteId, status: VideoCall['status']): void {
        const call = this.calls()[siteId];
        if (call) this.put({ ...call, status });
    }

    private put(call: VideoCall): void {
        this.calls.update((calls) => ({ ...calls, [call.siteId]: call }));
    }

    private notifyInvitationSent(): void {
        this.toaster.success({ title: this.translate.instant('TOASTER_VIDEO_CALL_INVITATION_SENT_MESSAGE') });
    }
}
